import { Controller } from "@hotwired/stimulus"

// Manages service worker update checks on the chat sync page.
// Shows current version, allows manual update check, and handles reload.
export default class extends Controller {
  static targets = ["status", "checkButton", "reloadButton", "reloadSection"]

  connect() {
    this._handler = () => this._onSwUpdated()
    document.addEventListener("sw:updated", this._handler)
    this._checkCurrentState()
  }

  disconnect() {
    document.removeEventListener("sw:updated", this._handler)
  }

  async checkForUpdate() {
    if (!("serviceWorker" in navigator)) {
      this._setStatus("Service workers not supported in this browser.")
      return
    }

    this._setStatus("Checking for updates...")
    if (this.hasCheckButtonTarget) this.checkButtonTarget.disabled = true

    try {
      const reg = await navigator.serviceWorker.getRegistration()
      if (!reg) {
        this._setStatus("No service worker registered yet.")
        if (this.hasCheckButtonTarget) this.checkButtonTarget.disabled = false
        return
      }

      await reg.update()

      if (reg.waiting) {
        this._setStatus("Update ready to install.")
        this._showReload()
        return
      }

      reg.addEventListener("updatefound", () => {
        const sw = reg.installing
        if (!sw) return
        sw.addEventListener("statechange", () => {
          if (sw.state === "installed") {
            this._setStatus("Update downloaded and ready.")
            this._showReload()
          }
        })
      })

      // Give it 4 seconds to find an update, then confirm up to date
      setTimeout(() => {
        if (!reg.waiting && !reg.installing) {
          this._setStatus("You're on the latest version.")
          if (this.hasCheckButtonTarget) this.checkButtonTarget.disabled = false
        }
      }, 4000)

    } catch (err) {
      this._setStatus("Could not check for updates. Try again when online.")
      if (this.hasCheckButtonTarget) this.checkButtonTarget.disabled = false
    }
  }

  reload() {
    window.location.reload()
  }

  // -- private --

  _setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  _showReload() {
    if (this.hasReloadSectionTarget) this.reloadSectionTarget.classList.remove("hidden")
    if (this.hasCheckButtonTarget)   this.checkButtonTarget.classList.add("hidden")
  }

  _onSwUpdated() {
    this._setStatus("New version available.")
    this._showReload()
  }

  async _checkCurrentState() {
    if (!("serviceWorker" in navigator)) return
    const reg = await navigator.serviceWorker.getRegistration().catch(() => null)
    if (reg?.waiting) {
      this._setStatus("Update ready to install.")
      this._showReload()
    }
  }
}
